import { type FC, useEffect, useMemo, useRef, useState } from "react";

import type {
  EnvironmentProfileOption,
  IssueDetail,
  RepositoryBranch,
  RepositoryOption,
  WizardEnvironmentSelection,
  WizardRepositorySelection,
} from "./types";

import { InstanceAvatar } from "../InstanceAvatar";
import { ModelChip } from "../ModelChip";
import { RetroError, SectionLabel } from "../retro";
import { DEFAULT_CHANNEL_ENVIRONMENT_FORM } from "../useChannelEnvironment";
import { type ActorTemplate, useTemplates } from "../useTemplates";
import {
  discoverBranches,
  listEnvironmentProfiles,
  listRepositories,
} from "./api";
import { sanitizeChannelName } from "./channelName";

/** The subset of a template the dialog's selection state actually uses;
 * lets stale selections (template deleted mid-flight) still be removed. */
export type TemplateSelectionTarget = Pick<ActorTemplate, "id" | "name">;

interface SetupStepProps {
  issue: IssueDetail | null;
  selections: { templateId: string; displayName: string }[];
  leadTemplateId: string | null;
  environment: WizardEnvironmentSelection | null;
  /** True once the user has explicitly enabled/disabled the sandbox, so the
   * auto-default never overrides a deliberate choice across step changes. */
  environmentTouched: boolean;
  /** Repo selections stashed while the sandbox is off — owned by the dialog
   * because this step unmounts on the review step. */
  savedRepos: WizardRepositorySelection[];
  onSaveRepos: (repositories: WizardRepositorySelection[]) => void;
  channelName: string;
  generatedChannelName: string;
  onToggleTemplate: (template: TemplateSelectionTarget) => void;
  onSetLead: (templateId: string) => void;
  onSetEnvironment: (environment: WizardEnvironmentSelection | null) => void;
  onSetEnvironmentTouched: () => void;
  onSetChannelName: (name: string) => void;
  onValidityChange: (valid: boolean) => void;
}

type LoadStatus = "loading" | "error" | "ready";

interface BranchPickerState {
  repoKey: string;
  status: LoadStatus;
  branches: RepositoryBranch[];
  filter: string;
}

function formatMb(mb: number): string {
  return mb % 1024 === 0 ? `${mb / 1024} GB` : `${mb} MB`;
}

/** Unavailable-profile notes shown under the sandbox toggle. The server's
 * `unavailableMessage` is the whole explanation — the wizard has no reason-code
 * branch, so any new admission reason surfaces here without a UI change. */
export function unavailableProfileNotes(
  profiles: EnvironmentProfileOption[],
): { profileId: string; text: string }[] {
  return profiles
    .filter((profile) => !profile.selectable && profile.unavailableMessage)
    .map((profile) => ({
      profileId: profile.profileId,
      text: `${profile.displayName}: ${profile.unavailableMessage}`,
    }));
}

/** Step 2 — outcome-first setup: a recommended lead preselected, the shared
 * sandbox on by default when a profile is available, channel name demoted to
 * a compact confirm-or-edit row. */
export const SetupStep: FC<SetupStepProps> = ({
  issue,
  selections,
  leadTemplateId,
  environment,
  environmentTouched,
  savedRepos,
  onSaveRepos,
  channelName,
  generatedChannelName,
  onToggleTemplate,
  onSetLead,
  onSetEnvironment,
  onSetEnvironmentTouched,
  onSetChannelName,
  onValidityChange,
}) => {
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    reload: reloadTemplates,
  } = useTemplates();
  const [profiles, setProfiles] = useState<EnvironmentProfileOption[]>([]);
  const [profileStatus, setProfileStatus] = useState<LoadStatus>("loading");
  const [profileError, setProfileError] = useState<string | null>(null);
  const [repositories, setRepositories] = useState<RepositoryOption[]>([]);
  const [repoStatus, setRepoStatus] = useState<LoadStatus>("loading");
  const [repoError, setRepoError] = useState<string | null>(null);
  const [repoQuery, setRepoQuery] = useState("");
  const [branchPicker, setBranchPicker] = useState<BranchPickerState | null>(
    null,
  );
  // One fetch per repo per dialog: branch lists are a remote provider call.
  const branchCacheRef = useRef(new Map<string, RepositoryBranch[]>());
  const [editingName, setEditingName] = useState(false);
  const preEditNameRef = useRef("");
  const preselectedLeadRef = useRef(false);

  const selectedTemplateIds = useMemo(
    () => selections.map((entry) => entry.templateId),
    [selections],
  );

  const loadProfiles = () => {
    setProfileStatus("loading");
    setProfileError(null);
    listEnvironmentProfiles()
      .then((loaded) => {
        setProfiles(loaded);
        setProfileStatus("ready");
      })
      .catch((error: unknown) => {
        setProfileError(error instanceof Error ? error.message : String(error));
        setProfileStatus("error");
      });
  };

  const loadRepositories = () => {
    setRepoStatus("loading");
    setRepoError(null);
    listRepositories()
      .then((loaded) => {
        setRepositories(loaded);
        setRepoStatus("ready");
      })
      .catch((error: unknown) => {
        setRepoError(error instanceof Error ? error.message : String(error));
        setRepoStatus("error");
      });
  };

  useEffect(() => {
    loadProfiles();
    loadRepositories();
    // Independent fetches with their own retries; a profile failure must not
    // hide repositories or vice versa.
  }, []);

  // Stable order: recommended templates first (lead before member), then
  // alphabetical. Frozen per load — never reorders as selection changes.
  const orderedTemplates = useMemo(() => {
    const ranks: Record<string, number> = { lead: 0, member: 1 };
    const rank = (template: ActorTemplate) =>
      ranks[template.issueWorkspaceDefault ?? ""] ?? 2;
    return templates.toSorted(
      (a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name),
    );
  }, [templates]);

  // Preselect the org's recommended lead once, only while nothing is chosen.
  useEffect(() => {
    if (
      preselectedLeadRef.current ||
      templatesLoading ||
      selections.length > 0
    ) {
      return;
    }
    const recommended = templates.find(
      (template) => template.issueWorkspaceDefault === "lead",
    );
    if (recommended) {
      preselectedLeadRef.current = true;
      onToggleTemplate(recommended);
    }
  }, [templatesLoading, templates, selections, onToggleTemplate]);

  // A selection whose template vanished (deleted / realtime refresh) blocks
  // advancing; the user removes it explicitly rather than us silently
  // changing the launch.
  const staleSelections = useMemo(() => {
    // A successfully loaded empty catalog is authoritative: every selection
    // is stale. Only an unresolved or failed load withholds judgment.
    if (templatesLoading || templatesError) {
      return [];
    }
    const known = new Set(templates.map((template) => template.id));
    return selections.filter((entry) => !known.has(entry.templateId));
  }, [templatesLoading, templatesError, templates, selections]);

  const selectableProfiles = useMemo(
    () => profiles.filter((profile) => profile.selectable),
    [profiles],
  );

  // An enabled sandbox whose profile vanished or lost capacity would fail at
  // launch; block advancing until it's disabled or switched.
  const environmentProfileUnavailable =
    environment !== null &&
    profileStatus === "ready" &&
    !selectableProfiles.some(
      (profile) => profile.profileId === environment.profileId,
    );

  // Existing selections can't be trusted until the catalog authoritatively
  // resolves — otherwise a deleted template slips through during a remount's
  // loading window (validity must fail closed, not open).
  const selectionsUnverified =
    selections.length > 0 && (templatesLoading || Boolean(templatesError));

  useEffect(() => {
    onValidityChange(
      !selectionsUnverified &&
        staleSelections.length === 0 &&
        !environmentProfileUnavailable,
    );
  }, [
    selectionsUnverified,
    staleSelections,
    environmentProfileUnavailable,
    onValidityChange,
  ]);

  const requiredRepoSelections = (): WizardRepositorySelection[] =>
    repositories
      .filter((repo) => repo.isRequired)
      .map((repo) => ({
        defaultBranch: repo.defaultBranch,
        permission: "write" as const,
        providerId: repo.providerId,
        repositoryId: repo.repositoryId,
      }));

  const enableSandbox = (
    profile: EnvironmentProfileOption,
    repositorySelections?: WizardRepositorySelection[],
  ) => {
    onSetEnvironment({
      cpuCount:
        profile.defaults?.cpuCount ?? DEFAULT_CHANNEL_ENVIRONMENT_FORM.cpuCount,
      profileId: profile.profileId,
      ramMB: profile.defaults?.ramMB ?? DEFAULT_CHANNEL_ENVIRONMENT_FORM.ramMB,
      repositories:
        repositorySelections ??
        (savedRepos.length > 0 ? savedRepos : requiredRepoSelections()),
      storageMB:
        profile.defaults?.storageMB ??
        DEFAULT_CHANNEL_ENVIRONMENT_FORM.storageMB,
    });
  };

  // Sandbox on by default once profiles resolve — the wizard exists to hand
  // over a ready workspace. Never fires after a deliberate user choice.
  useEffect(() => {
    if (
      environmentTouched ||
      environment !== null ||
      profileStatus !== "ready" ||
      repoStatus === "loading" ||
      selectableProfiles.length === 0
    ) {
      return;
    }
    enableSandbox(selectableProfiles[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    environmentTouched,
    environment,
    profileStatus,
    repoStatus,
    selectableProfiles,
  ]);

  const setSandboxEnabled = (enabled: boolean) => {
    onSetEnvironmentTouched();
    setBranchPicker(null);
    if (!enabled) {
      if (environment) {
        onSaveRepos(environment.repositories);
      }
      onSetEnvironment(null);
      return;
    }
    if (environment === null && selectableProfiles.length > 0) {
      enableSandbox(selectableProfiles[0]);
    }
  };

  const toggleRepository = (repo: RepositoryOption) => {
    if (!environment) {
      return;
    }
    const exists = environment.repositories.some(
      (candidate) =>
        candidate.providerId === repo.providerId &&
        candidate.repositoryId === repo.repositoryId,
    );
    if (
      exists &&
      branchPicker?.repoKey === `${repo.providerId}:${repo.repositoryId}`
    ) {
      setBranchPicker(null);
    }
    onSetEnvironment({
      ...environment,
      repositories: exists
        ? environment.repositories.filter(
            (candidate) =>
              candidate.providerId !== repo.providerId ||
              candidate.repositoryId !== repo.repositoryId,
          )
        : [
            ...environment.repositories,
            {
              defaultBranch: repo.defaultBranch,
              permission: "write",
              providerId: repo.providerId,
              repositoryId: repo.repositoryId,
            },
          ],
    });
  };

  const setRepositoryBranch = (
    repo: RepositoryOption,
    branch: string | null,
  ) => {
    if (!environment) {
      return;
    }
    onSetEnvironment({
      ...environment,
      repositories: environment.repositories.map((candidate) =>
        candidate.providerId === repo.providerId &&
        candidate.repositoryId === repo.repositoryId
          ? { ...candidate, defaultBranch: branch }
          : candidate,
      ),
    });
  };

  const toggleBranchPicker = (repo: RepositoryOption) => {
    const repoKey = `${repo.providerId}:${repo.repositoryId}`;
    if (branchPicker?.repoKey === repoKey) {
      setBranchPicker(null);
      return;
    }
    // No slug → the provider can't list branches; go straight to free text.
    if (!repo.remoteSlug) {
      setBranchPicker({ branches: [], filter: "", repoKey, status: "error" });
      return;
    }
    const cached = branchCacheRef.current.get(repoKey);
    if (cached) {
      setBranchPicker({
        branches: cached,
        filter: "",
        repoKey,
        status: "ready",
      });
      return;
    }
    setBranchPicker({ branches: [], filter: "", repoKey, status: "loading" });
    discoverBranches(repo.providerId, repo.remoteSlug)
      .then((discovered) => {
        // GitHub returns branches alphabetically; hoist the default so the
        // most likely pick is always first.
        const branches = discovered.toSorted(
          (a, b) => Number(b.isDefault) - Number(a.isDefault),
        );
        branchCacheRef.current.set(repoKey, branches);
        setBranchPicker((current) =>
          current?.repoKey === repoKey
            ? { ...current, branches, status: "ready" }
            : current,
        );
      })
      .catch(() => {
        setBranchPicker((current) =>
          current?.repoKey === repoKey
            ? { ...current, status: "error" }
            : current,
        );
      });
  };

  const activeProfile = environment
    ? (profiles.find(
        (profile) => profile.profileId === environment.profileId,
      ) ?? null)
    : null;

  const filteredRepositories = useMemo(() => {
    const query = repoQuery.trim().toLowerCase();
    if (!query) {
      return repositories;
    }
    return repositories.filter((repo) =>
      repo.displayName.toLowerCase().includes(query),
    );
  }, [repositories, repoQuery]);

  const unavailableProfiles = unavailableProfileNotes(profiles);

  const sanitizedPreview =
    sanitizeChannelName(channelName) || generatedChannelName;

  return (
    <div className="space-y-5">
      {issue && (
        <div className="truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
          <span className="font-bold text-emerald-600 dark:text-emerald-400">
            {issue.issueKey}
          </span>
          <span className="mx-1.5">·</span>
          {issue.summary}
        </div>
      )}

      <div>
        <SectionLabel label="Team" />
        <p className="mt-0.5 font-mono text-[10px] text-slate-500 dark:text-slate-400">
          Choose who starts on this ticket. The lead receives the ticket first;
          add specialists only when useful.
        </p>

        {templatesLoading && (
          <div className="mt-2 space-y-1">
            {[0, 1].map((index) => (
              <div
                key={index}
                className="h-12 animate-pulse border border-slate-200 dark:border-slate-800 bg-slate-200/60 motion-reduce:animate-none dark:bg-slate-800/40"
              />
            ))}
          </div>
        )}

        {!templatesLoading && templatesError && (
          <div className="mt-2 space-y-2">
            <RetroError
              message={`Couldn't load templates: ${templatesError}`}
            />
            <button
              type="button"
              onClick={() => void reloadTemplates()}
              className="border border-slate-300 dark:border-slate-700 px-2 py-1 font-mono text-[11px] text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Retry
            </button>
          </div>
        )}

        {!templatesLoading && !templatesError && templates.length === 0 && (
          <div className="mt-2 border border-amber-500/30 bg-amber-500/5 px-3 py-2 font-mono text-[11px] text-amber-700 dark:text-amber-300">
            No agent templates yet.{" "}
            <a href="/agents-templates" className="underline">
              Open Templates
            </a>{" "}
            to create one first.
          </div>
        )}

        {!templatesLoading &&
          !templatesError &&
          (templates.length > 0 || staleSelections.length > 0) && (
            <div className="mt-2 space-y-1">
              {orderedTemplates.map((template) => {
                const selected = selectedTemplateIds.includes(template.id);
                const isLead = leadTemplateId === template.id;
                return (
                  <SetupTemplateRow
                    key={template.id}
                    template={template}
                    selected={selected}
                    isLead={isLead}
                    onToggleTemplate={onToggleTemplate}
                    onSetLead={onSetLead}
                  />
                );
              })}

              {staleSelections.map((entry) => (
                <div
                  key={entry.templateId}
                  className="flex items-center gap-3 border border-amber-500/40 bg-amber-500/5 px-3 py-2"
                >
                  <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-amber-700 dark:text-amber-300">
                    {entry.displayName} is no longer available
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      onToggleTemplate({
                        id: entry.templateId,
                        name: entry.displayName,
                      })
                    }
                    className="shrink-0 border border-amber-500/40 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 hover:bg-amber-500/10"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
      </div>

      <div>
        <SectionLabel label="Sandbox workspace" />

        {profileStatus === "loading" && (
          <div className="mt-2 font-mono text-[11px] text-slate-500 dark:text-slate-400">
            Loading sandbox profiles…
          </div>
        )}

        {profileStatus === "error" && (
          <div className="mt-2 space-y-2">
            <RetroError
              message={`Sandbox profiles unavailable: ${profileError ?? "unknown error"}. You can still launch without a sandbox.`}
            />
            <button
              type="button"
              onClick={loadProfiles}
              className="border border-slate-300 dark:border-slate-700 px-2 py-1 font-mono text-[11px] text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Retry
            </button>
          </div>
        )}

        {/* An enabled sandbox must stay controllable (at least: off) even
            when the profile list is loading or failed after a remount. */}
        {(profileStatus === "ready" || environment !== null) && (
          <div className="mt-2 space-y-2">
            <button
              type="button"
              role="switch"
              aria-checked={environment !== null}
              // Turning OFF must stay possible even with no selectable
              // profiles left, or a stranded environment can't be cleared.
              disabled={selectableProfiles.length === 0 && environment === null}
              onClick={() => setSandboxEnabled(environment === null)}
              className="flex w-full items-start gap-2.5 text-left disabled:cursor-not-allowed disabled:opacity-40"
            >
              <span
                className={`mt-0.5 shrink-0 font-mono text-[10px] ${
                  environment !== null
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-slate-600 dark:text-slate-500"
                }`}
              >
                {environment !== null ? "■" : "□"}
              </span>
              <span className="min-w-0">
                <span className="block text-xs text-slate-700 dark:text-slate-200">
                  Create shared sandbox
                </span>
                <span className="block font-mono text-[10px] text-slate-500 dark:text-slate-400">
                  Lets the team run commands and edit attached repositories.
                </span>
              </span>
            </button>

            {selectableProfiles.length === 0 && environment === null && (
              <div className="border border-amber-500/30 bg-amber-500/5 px-3 py-2 font-mono text-[11px] text-amber-700 dark:text-amber-300">
                No sandbox profile is currently available. Launching without a
                sandbox.
              </div>
            )}

            {environmentProfileUnavailable && (
              <div className="border border-amber-500/30 bg-amber-500/5 px-3 py-2 font-mono text-[11px] text-amber-700 dark:text-amber-300">
                The selected sandbox profile is no longer available. Switch
                profiles or disable the sandbox to continue.
              </div>
            )}

            {unavailableProfiles.map((note) => (
              <p
                key={note.profileId}
                className="font-mono text-[10px] text-slate-500 dark:text-slate-400"
              >
                {note.text}
              </p>
            ))}

            {environment !== null && selectableProfiles.length > 1 && (
              <div className="flex flex-wrap gap-1.5">
                {selectableProfiles.map((profile) => (
                  <button
                    key={profile.profileId}
                    type="button"
                    onClick={() => {
                      onSetEnvironmentTouched();
                      enableSandbox(profile, environment.repositories);
                    }}
                    className={`border px-2 py-1 font-mono text-[11px] transition-colors ${
                      environment.profileId === profile.profileId
                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"
                    }`}
                  >
                    {profile.displayName}
                  </button>
                ))}
              </div>
            )}

            {environment !== null && (
              <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {activeProfile?.displayName ?? environment.profileId} ·{" "}
                {environment.cpuCount} CPU · {formatMb(environment.ramMB)} RAM ·{" "}
                {formatMb(environment.storageMB)} workspace
              </p>
            )}

            {environment !== null && repoStatus === "error" && (
              <div className="space-y-2">
                <RetroError
                  message={`Repositories unavailable: ${repoError ?? "unknown error"}. The sandbox still works without them.`}
                />
                <button
                  type="button"
                  onClick={loadRepositories}
                  className="border border-slate-300 dark:border-slate-700 px-2 py-1 font-mono text-[11px] text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Retry
                </button>
              </div>
            )}

            {environment !== null &&
              repoStatus === "ready" &&
              repositories.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
                    Repositories (cloned into the sandbox)
                  </p>
                  {repositories.length > 8 && (
                    <input
                      type="text"
                      value={repoQuery}
                      onChange={(event) => setRepoQuery(event.target.value)}
                      placeholder="Filter repositories…"
                      className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1 font-mono text-[11px] text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
                    />
                  )}
                  <div className="mt-1 max-h-48 space-y-1 overflow-y-auto">
                    {filteredRepositories.map((repo) => {
                      const repoKey = `${repo.providerId}:${repo.repositoryId}`;
                      const attachment = environment.repositories.find(
                        (candidate) =>
                          candidate.providerId === repo.providerId &&
                          candidate.repositoryId === repo.repositoryId,
                      );
                      const active = attachment !== undefined;
                      const chosenBranch =
                        attachment?.defaultBranch ?? repo.defaultBranch;
                      const branchOverridden =
                        active &&
                        chosenBranch !== null &&
                        chosenBranch !== repo.defaultBranch;
                      return (
                        <SetupRepositoryRow
                          key={repoKey}
                          repoKey={repoKey}
                          repo={repo}
                          active={active}
                          chosenBranch={chosenBranch}
                          branchOverridden={branchOverridden}
                          branchPicker={branchPicker}
                          toggleRepository={toggleRepository}
                          toggleBranchPicker={toggleBranchPicker}
                          setBranchPicker={setBranchPicker}
                          setRepositoryBranch={setRepositoryBranch}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
        <SectionLabel label="Channel" />
        {editingName ? (
          <div className="mt-1">
            <input
              type="text"
              value={channelName}
              autoFocus
              onChange={(event) => onSetChannelName(event.target.value)}
              onBlur={() => setEditingName(false)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setEditingName(false);
                }
                if (event.key === "Escape") {
                  // Cancel the edit only — without stopPropagation the
                  // Modal's window-level Escape handler closes the wizard.
                  event.stopPropagation();
                  onSetChannelName(preEditNameRef.current);
                  setEditingName(false);
                }
              }}
              className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 font-mono text-xs text-slate-700 dark:text-slate-200"
            />
            <p className="mt-1 font-mono text-[10px] text-slate-500 dark:text-slate-400">
              Will be created as{" "}
              <span className="text-emerald-700 dark:text-emerald-300">
                #{sanitizedPreview}
              </span>
            </p>
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-2">
            <span className="truncate font-mono text-xs text-emerald-700 dark:text-emerald-300">
              #{sanitizedPreview}
            </span>
            <button
              type="button"
              onClick={() => {
                if (!channelName) {
                  onSetChannelName(generatedChannelName);
                }
                preEditNameRef.current = channelName || generatedChannelName;
                setEditingName(true);
              }}
              className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Edit
            </button>
            {channelName && channelName !== generatedChannelName && (
              <button
                type="button"
                onClick={() => onSetChannelName("")}
                className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Reset
              </button>
            )}
          </div>
        )}
        <p className="mt-1 font-mono text-[10px] text-slate-600 dark:text-slate-500">
          The server may add a numeric suffix if the name is taken.
        </p>
      </div>
    </div>
  );
};

interface SetupTemplateRowProps {
  template: ActorTemplate;
  selected: boolean;
  isLead: boolean;
  onToggleTemplate: SetupStepProps["onToggleTemplate"];
  onSetLead: SetupStepProps["onSetLead"];
}

function SetupTemplateRow({
  template,
  selected,
  isLead,
  onToggleTemplate,
  onSetLead,
}: SetupTemplateRowProps) {
  return (
    <div
      key={template.id}
      className={`flex items-start gap-3 border px-3 py-2 transition-colors ${
        selected
          ? "border-emerald-500/50 bg-emerald-500/5"
          : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600"
      }`}
    >
      <button
        type="button"
        onClick={() => onToggleTemplate(template)}
        className="flex min-w-0 flex-1 items-start gap-2.5 text-left"
      >
        <span
          className={`mt-0.5 font-mono text-[10px] ${selected ? "text-emerald-600 dark:text-emerald-400" : "text-slate-600 dark:text-slate-500"}`}
        >
          {selected ? "■" : "□"}
        </span>
        <span className="mt-px shrink-0">
          <InstanceAvatar seed={template.avatarSeed} size="sm" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate text-xs text-slate-700 dark:text-slate-200">
              {template.name}
            </span>
            {template.modelRef && (
              <span className="shrink-0">
                <ModelChip modelRef={template.modelRef} />
              </span>
            )}
          </span>
          {template.description && (
            <span className="mt-0.5 line-clamp-2 block font-mono text-[10px] leading-snug text-slate-500 dark:text-slate-400">
              {template.description}
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        onClick={() => {
          if (!selected) {
            onToggleTemplate(template);
          }
          onSetLead(template.id);
        }}
        className={`mt-0.5 shrink-0 border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${
          isLead
            ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
            : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
        }`}
      >
        {isLead ? "Lead" : "Make lead"}
      </button>
    </div>
  );
}

interface RepositoryBranchPickerProps {
  branchPicker: BranchPickerState;
  repo: RepositoryOption;
  chosenBranch: string | null;
  branchOverridden: boolean;
  setBranchPicker: (state: BranchPickerState | null) => void;
  setRepositoryBranch: (repo: RepositoryOption, branch: string | null) => void;
}

function RepositoryBranchPicker({
  branchPicker,
  repo,
  chosenBranch,
  branchOverridden,
  setBranchPicker,
  setRepositoryBranch,
}: RepositoryBranchPickerProps) {
  return (
    <div className="space-y-1 border border-t-0 border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/60 px-2 py-1.5">
      {branchPicker.status === "loading" && (
        <p className="font-mono text-[10px] text-slate-500 dark:text-slate-400">
          Loading branches…
        </p>
      )}
      {branchPicker.status === "error" && (
        <div className="space-y-1">
          <p className="font-mono text-[10px] text-amber-700 dark:text-amber-300">
            Couldn't list branches. Type one instead.
          </p>
          <input
            type="text"
            autoFocus
            placeholder={repo.defaultBranch ?? "branch"}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setBranchPicker(null);
              }
              if (event.key === "Enter") {
                const value = event.currentTarget.value.trim();
                if (value) {
                  setRepositoryBranch(repo, value);
                }
                setBranchPicker(null);
              }
            }}
            className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1 font-mono text-[10px] text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
          />
        </div>
      )}
      {branchPicker.status === "ready" && (
        <>
          {branchPicker.branches.length > 8 && (
            <input
              type="text"
              value={branchPicker.filter}
              autoFocus
              onChange={(event) =>
                setBranchPicker({ ...branchPicker, filter: event.target.value })
              }
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.stopPropagation();
                  setBranchPicker(null);
                }
              }}
              placeholder="Filter branches…"
              className="w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1 font-mono text-[10px] text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
            />
          )}
          <div className="max-h-28 space-y-0.5 overflow-y-auto">
            {branchPicker.branches
              .filter((branch) =>
                branch.name
                  .toLowerCase()
                  .includes(branchPicker.filter.trim().toLowerCase()),
              )
              .map((branch) => (
                <button
                  key={branch.name}
                  type="button"
                  onClick={() => {
                    setRepositoryBranch(repo, branch.name);
                    setBranchPicker(null);
                  }}
                  className={`flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${
                    branch.name === chosenBranch
                      ? "text-sky-700 dark:text-sky-300"
                      : "text-slate-500 dark:text-slate-400"
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{branch.name}</span>
                  {branch.isDefault && (
                    <span className="shrink-0 text-[10px] uppercase tracking-wider text-slate-600 dark:text-slate-500">
                      default
                    </span>
                  )}
                </button>
              ))}
          </div>
        </>
      )}
      {branchOverridden && repo.defaultBranch && (
        <button
          type="button"
          onClick={() => {
            setRepositoryBranch(repo, repo.defaultBranch);
            setBranchPicker(null);
          }}
          className="w-full border border-slate-300 dark:border-slate-700 px-1.5 py-0.5 text-left font-mono text-[10px] text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Reset to {repo.defaultBranch}
        </button>
      )}
    </div>
  );
}

interface SetupRepositoryRowProps {
  repoKey: string;
  repo: RepositoryOption;
  active: boolean;
  chosenBranch: string | null;
  branchOverridden: boolean;
  branchPicker: BranchPickerState | null;
  toggleRepository: (repo: RepositoryOption) => void;
  toggleBranchPicker: (repo: RepositoryOption) => void;
  setBranchPicker: (state: BranchPickerState | null) => void;
  setRepositoryBranch: (repo: RepositoryOption, branch: string | null) => void;
}

function SetupRepositoryRow({
  repoKey,
  repo,
  active,
  chosenBranch,
  branchOverridden,
  branchPicker,
  toggleRepository,
  toggleBranchPicker,
  setBranchPicker,
  setRepositoryBranch,
}: SetupRepositoryRowProps) {
  return (
    <div key={repoKey}>
      <div
        className={`flex w-full items-center gap-2 border px-2 py-1 font-mono text-[10px] transition-colors ${
          active
            ? "border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-300"
            : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"
        }`}
      >
        <button
          type="button"
          onClick={() => toggleRepository(repo)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span>{active ? "■" : "□"}</span>
          <span className="min-w-0 flex-1 truncate">
            {repo.displayName}
            {!active && repo.defaultBranch && (
              <span className="text-slate-500 dark:text-slate-400">
                {" "}
                · {repo.defaultBranch}
              </span>
            )}
          </span>
        </button>
        {active && (
          <button
            type="button"
            onClick={() => toggleBranchPicker(repo)}
            title="Change branch"
            className={`shrink-0 border px-1.5 py-0.5 transition-colors ${
              branchOverridden
                ? "border-amber-500/50 text-amber-700 dark:text-amber-300 hover:border-amber-500 dark:hover:border-amber-400"
                : "border-sky-500/40 text-sky-700/80 dark:text-sky-300/80 hover:border-sky-500 dark:hover:border-sky-400"
            }`}
          >
            {chosenBranch ?? "choose branch"} ▾
          </button>
        )}
      </div>
      {active && branchPicker?.repoKey === repoKey && (
        <RepositoryBranchPicker
          branchPicker={branchPicker}
          repo={repo}
          chosenBranch={chosenBranch}
          branchOverridden={branchOverridden}
          setBranchPicker={setBranchPicker}
          setRepositoryBranch={setRepositoryBranch}
        />
      )}
    </div>
  );
}
